import { useEffect, useRef, useState } from "react";

import { Alert, Snackbar, Typography } from "@mui/material";
import Slide from "@mui/material/Slide";
import { isNil } from "lodash";
import { DateTime } from "luxon";
import { usePostHog } from "posthog-js/react";

import { SuccessMessageDialog } from "components/SuccessMessageDialog";
import {
  useAuthContext,
  useCarrierCompanyBookingLimits,
  useCarrierShipmentPageContext,
  useDisclosure,
  useShipmentQuotes,
  useWriterComponentState,
} from "hooks";

import { NoBookingsRemainingModal } from "./components/NoBookingsRemainingModal";
import { SubmitQuoteForm } from "./SubmitQuoteForm";
import { APIErrorCode, ClientAppEvent } from "../../../../constants";
import {
  carrierHasLostShipment,
  extractDateFromIsoString,
  getSubmitQuoteBlockMessage,
  getSubmitQuoteBlockReason,
  naiveDateToISOString,
  startLessOrEqualToEnd,
} from "../../../../utils";

import type { SubmitQuoteFormState } from "./types";

interface Props {
  shipment: CarrierCondensedShipment;
  secondaryAction?: React.ReactNode;
}

const INIITAL_STATE: SubmitQuoteFormState = {
  equipmentType: "",
  pickupStart: "",
  pickupStartTime: "",
  deliveryEta: "",
  deliveryEtaTime: "",
  currency: "CAD",
  price: null,
  notes: "",
  trackingLinkOffered: false,
};

const validateTimePoints = (pickupStart: string, type: ShipmentType) => {
  if (type === "QUOTES_ONLY") {
    return DateTime.now().toFormat("yyyy-MM-dd");
  }
  if (pickupStart.length === 0) {
    return "";
  }

  return naiveDateToISOString(pickupStart);
};

export function SubmitQuoteFormContainer(props: Props) {
  const { shipment, secondaryAction } = props;
  const { user } = useAuthContext();
  const posthog = usePostHog();
  const {
    isOpen: quoteSentDialogOpen,
    onClose: onCloseQuoteSentDialog,
    onOpen: onOpenQuoteSentDialog,
  } = useDisclosure(false);

  const {
    isOpen: snackbarOpened,
    onClose: onSnackbarClose,
    onOpen: openSnackbar,
  } = useDisclosure(false);

  const {
    isOpen: carrierTeammatesOpened,
    onClose: closeCarrierTeammates,
    onOpen: openCarrierTeammates,
  } = useDisclosure(false);

  const { onRefresh } = useCarrierShipmentPageContext();
  const { limits, refetchLimits } = useCarrierCompanyBookingLimits();

  const [validationError, setValidationError] = useState<string | null>(null);
  const [formState, setFormState] =
    useState<SubmitQuoteFormState>(INIITAL_STATE);
  const [showEssentialsNoBookingModal, setShowEssentialsNoBookingModal] =
    useState<boolean>(false);

  const shipmentRef = useRef<undefined | CondensedShipment>();

  const { quotes, isLoading: quoteRefetchLoading } = useShipmentQuotes(
    shipment?.info.id ?? 0,
  );

  const findCarrierActiveShipment = (q: CondensedQuote) =>
    (q.internal_state === "ACTIVE" || q.internal_state === "COUNTERED") &&
    q.created_by.id === user?.id;

  const hasActiveQuote = quotes.some((q) => findCarrierActiveShipment(q));
  const shipmentBookedElsewhere = carrierHasLostShipment(shipment);
  const canSubmitQuote = !hasActiveQuote && !shipmentBookedElsewhere;
  const submitBlockReason = getSubmitQuoteBlockReason(
    hasActiveQuote,
    shipmentBookedElsewhere,
  );

  useEffect(() => {
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      setFormState(INIITAL_STATE);
      refetchLimits();
    }
  }, [shipment, refetchLimits]);

  useEffect(() => {
    if (
      shipment &&
      shipmentRef.current &&
      shipmentRef.current.version !== shipment.version
    ) {
      shipmentRef.current = shipment;
      refetchLimits();
    }
  }, [shipment, refetchLimits]);

  const onValueChanged = <T,>(
    key: keyof SubmitQuoteFormState,
    value: T,
  ): void => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onPopulateShipmentRequirements = () => {
    onValueChanged(
      "pickupStart",
      extractDateFromIsoString(shipment.first_point.start_time),
    );
    onValueChanged(
      "deliveryEta",
      extractDateFromIsoString(shipment.last_point.start_time),
    );

    if (shipment.equipments.length > 0) {
      onValueChanged("equipmentType", shipment.equipments[0].type);
    }
    openSnackbar();
  };

  const onWriteSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
    if (
      limits != null &&
      limits.remainingBookings < 1 &&
      !shipment.is_partner_shipment
    ) {
      setShowEssentialsNoBookingModal(true);
    } else {
      onOpenQuoteSentDialog();
    }
  };

  const onWriteFailure = (e: Error) => {
    const axiosError = e as { response?: { data?: { code?: string } } };
    const errorCode = axiosError?.response?.data?.code;
    if (errorCode === APIErrorCode.NO_CARRIER_INSURANCE) {
      posthog?.capture(ClientAppEvent.CARRIER_QUOTE_REJECTED_NO_INSURANCE, {
        shipment_id: shipment.info.id,
      });
    } else if (errorCode === APIErrorCode.EXPIRED_CARRIER_INSURANCE) {
      posthog?.capture(
        ClientAppEvent.CARRIER_QUOTE_REJECTED_EXPIRED_INSURANCE,
        {
          shipment_id: shipment.info.id,
        },
      );
    }
  };

  const {
    loading,
    error: writeError,
    success: quoteSubmitted,
    write,
  } = useWriterComponentState({
    errorMessage:
      "Failed to submit quote. Please contact us if the problem persists.",
    onWriteFailure,
    writeConfig: {
      method: "post",
      url: `/shipments/${shipment.info.id}/quotes`,
      body: {
        equipment_type: formState.equipmentType,
        pickup_start: validateTimePoints(
          formState.pickupStart,
          shipment.info.type,
        ),
        pickup_start_time: null,
        delivery_eta: validateTimePoints(
          formState.deliveryEta,
          shipment.info.type,
        ),
        delivery_eta_time: null,
        price: formState.price,
        currency: formState.currency,
        notes: formState.notes,
        tracking_link_offered: formState.trackingLinkOffered,
        quoting_unit_of_measure: shipment.info.quoting_unit_of_measure,
      },
    },
  });

  const onSubmit = () => {
    if (isNil(formState.price) || formState.price <= 0) {
      setValidationError("Price must be greater than 0.");
      return;
    }

    if (
      shipment.info.type !== "QUOTES_ONLY" &&
      !startLessOrEqualToEnd(formState.pickupStart, formState.deliveryEta)
    ) {
      setValidationError(
        "Pickup start must be before or equal to the delivery ETA.",
      );
      return;
    }

    if (formState.equipmentType === "") {
      setValidationError("Please select an equipment type.");
      return;
    }

    // Re-check eligibility here, not just via the disabled prop passed down to
    // the form: the carrier's quotes (or the shipment) may have changed since
    // that prop was last rendered.
    if (!canSubmitQuote) {
      setValidationError(
        submitBlockReason != null
          ? getSubmitQuoteBlockMessage(submitBlockReason)
          : "You can no longer submit a quote on this shipment.",
      );
      return;
    }

    setValidationError(null);

    const quoteType =
      shipment.info.type === "QUOTES_ONLY" ? "quotes_only" : "single";
    posthog?.capture(ClientAppEvent.QUOTE_SUBMITTED, {
      quote_type: quoteType,
      shipment_id: shipment.info.id,
    });

    write(onWriteSuccess);
  };

  return (
    <>
      <SuccessMessageDialog
        onClose={onCloseQuoteSentDialog}
        open={quoteSentDialogOpen}
        redirect={false}
        onCloseCallback={onRefresh ?? undefined}
        dialogBody={
          <Typography variant="h5">
            The shipper has received your quote. You will be notified
            immediately if they book your truck or send you a counteroffer. If
            you book your truck on another load, please be sure to return and
            cancel this quote.
          </Typography>
        }
        buttonVariant="contained"
        buttonLabel="Sounds good!"
      />
      <NoBookingsRemainingModal
        onClose={() => setShowEssentialsNoBookingModal(false)}
        open={showEssentialsNoBookingModal}
        onCloseCallback={onRefresh ?? null}
      />
      <SubmitQuoteForm
        onValueChanged={onValueChanged}
        enableSubmit
        onSubmit={onSubmit}
        error={validationError ?? writeError}
        loading={loading}
        quoteSubmitted={quoteSubmitted}
        canSubmitQuote={canSubmitQuote}
        submitBlockReason={submitBlockReason}
        formState={formState}
        quoteRefetchLoading={quoteRefetchLoading}
        onPopulateShipmentRequirements={onPopulateShipmentRequirements}
        shipment={shipment}
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        openCarrierTeammates={openCarrierTeammates}
        secondaryAction={secondaryAction}
      />
      <Snackbar
        id="customerRequierementsPopulatedSnackbar"
        open={snackbarOpened}
        autoHideDuration={2000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={onSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Requirements applied!
        </Alert>
      </Snackbar>
    </>
  );
}
